import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import {
  TREASURY_FIELD_USAGES,
  TREASURY_MOVEMENT_TYPES,
} from "../constants/treasuryFormsOptions";
import { categoryMatchesMovement } from "../utils/treasuryFormEditorModel";

const roundedInputSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "16px" },
};

function optionLabel(item) {
  const inactive = !item.isActive || item.deletedAt;
  return `${item.name}${inactive ? " (Inactiva)" : ""}`;
}

export default function TreasuryFormIntegrationEditor({
  field,
  errors = {},
  accounts,
  categories,
  loading,
  loadError,
  onChange,
}) {
  const movementEnabled = field.treasuryUsage === TREASURY_FIELD_USAGES.MOVEMENT;
  const visibleAccounts = accounts.filter(
    (account) =>
      (account.isActive && !account.deletedAt) ||
      String(account.id) === String(field.accountId)
  );
  const visibleCategories = categories.filter(
    (category) =>
      ((category.isActive && !category.deletedAt) ||
        String(category.id) === String(field.categoryId)) &&
      categoryMatchesMovement(category, field.movementType)
  );

  const handleUsageChange = (event) => {
    const treasuryUsage = event.target.value;
    onChange({
      ...field,
      treasuryUsage,
      ...(treasuryUsage === TREASURY_FIELD_USAGES.INFORMATIONAL
        ? {
            movementType: "",
            accountId: "",
            categoryId: "",
            concept: "",
            account: null,
            category: null,
          }
        : {}),
    });
  };

  const handleMovementTypeChange = (event) => {
    const movementType = event.target.value;
    const selectedCategory = categories.find(
      (category) => String(category.id) === String(field.categoryId)
    );
    const keepCategory = categoryMatchesMovement(selectedCategory, movementType);
    onChange({
      ...field,
      movementType,
      categoryId: keepCategory ? field.categoryId : "",
      category: keepCategory ? field.category : null,
    });
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mt: 1,
        border: "1px solid rgba(20, 32, 82, 0.12)",
        borderRadius: "16px !important",
        bgcolor: "rgba(245,247,255,0.75)",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20, color: "primary.700" }} />
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: "primary.900" }}>
              Integración con Tesorería
            </Typography>
            <Typography sx={{ fontSize: 11, color: "neutral.600" }}>
              {movementEnabled ? "Generará movimientos" : "Uso informativo"}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1.5}>
          {loadError ? <Alert severity="warning">{loadError}</Alert> : null}

          <TextField
            select
            size="small"
            label="Uso en Tesorería"
            value={field.treasuryUsage}
            onChange={handleUsageChange}
            sx={roundedInputSx}
            fullWidth
          >
            <MenuItem value={TREASURY_FIELD_USAGES.INFORMATIONAL}>Informativo</MenuItem>
            <MenuItem value={TREASURY_FIELD_USAGES.MOVEMENT}>Generar movimiento</MenuItem>
          </TextField>

          {movementEnabled ? (
            <>
              <TextField
                select
                size="small"
                label="Tipo de movimiento"
                value={field.movementType}
                onChange={handleMovementTypeChange}
                error={Boolean(errors.movementType)}
                helperText={errors.movementType}
                sx={roundedInputSx}
                fullWidth
              >
                <MenuItem value={TREASURY_MOVEMENT_TYPES.INCOME}>Ingreso</MenuItem>
                <MenuItem value={TREASURY_MOVEMENT_TYPES.EXPENSE}>Egreso</MenuItem>
              </TextField>

              <TextField
                select
                size="small"
                label="Cuenta"
                value={field.accountId}
                onChange={(event) =>
                  onChange({ ...field, accountId: event.target.value, account: null })
                }
                disabled={loading}
                error={Boolean(errors.accountId)}
                helperText={errors.accountId || (loading ? "Cargando cuentas..." : "")}
                sx={roundedInputSx}
                fullWidth
              >
                {visibleAccounts.map((account) => (
                  <MenuItem
                    key={account.id}
                    value={String(account.id)}
                    disabled={!account.isActive || Boolean(account.deletedAt)}
                  >
                    {optionLabel(account)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Categoría"
                value={field.categoryId}
                onChange={(event) =>
                  onChange({ ...field, categoryId: event.target.value, category: null })
                }
                disabled={loading || !field.movementType}
                error={Boolean(errors.categoryId)}
                helperText={
                  errors.categoryId ||
                  (!field.movementType ? "Primero seleccioná ingreso o egreso." : "")
                }
                sx={roundedInputSx}
                fullWidth
              >
                {visibleCategories.map((category) => (
                  <MenuItem
                    key={category.id}
                    value={String(category.id)}
                    disabled={!category.isActive || Boolean(category.deletedAt)}
                  >
                    {optionLabel(category)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                label="Concepto del movimiento"
                value={field.concept}
                onChange={(event) => onChange({ ...field, concept: event.target.value })}
                inputProps={{ maxLength: 255 }}
                error={Boolean(errors.concept)}
                helperText={errors.concept}
                sx={roundedInputSx}
                fullWidth
              />
            </>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
